// React Query
import {
  UseMutationResult,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
// Http Methods
import {
  getRequest as get,
  postRequest as post,
  putRequest as put,
} from '../utils/HttpMethods';
// Interfaces
import { AllTariffSearchResult, Tariff, isTariff } from 'interfaces';
// Sentry
import { tariffSchema } from '@billing/validation/tariff';
import type {
  BulkTariffIngestJob,
  TariffIngestSource,
} from '@billing/validation/tariffIngestion';
import { bulkTariffIngestJobSchema } from '@billing/validation/tariffIngestion';
import queryString from 'api/utils/queryStringBuilder';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import {
  convertFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';
import {
  PaginatedResult,
  PaginationParams,
  createEntity,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
  usePaginatedQuery,
} from '../utils/queries';

export const tariffQueryKeys = {
  all: ['tariffs'],
  list: () => [...tariffQueryKeys.all, 'list'],
  paginated: (
    page: number,
    pageSize: number,
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string,
    sorting?: MRT_SortingState
  ) => [
    ...tariffQueryKeys.all,
    'paginated',
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  get: (id: string) => [...tariffQueryKeys.all, id],
  search: (
    searchValue: string,
    specialtyCode: string | undefined,
    serviceStart: string | undefined,
    province: string | undefined
  ) => [
    ...tariffQueryKeys.all,
    'search',
    searchValue,
    specialtyCode,
    serviceStart,
    province,
  ],
  allSearch: (code: string) => [...tariffQueryKeys.all, 'search-all', code],
  provider: (providerID: string, userID?: string, siteID?: string) => [
    ...tariffQueryKeys.all,
    'provider',
    providerID,
    userID,
    siteID,
  ],
};
/**
 * @description Creates a new tariff.
 * @param {Tariff} tariff - The tariff data.
 * @returns {Promise<Tariff>} The created tariff.
 */
export const createTariff = async (tariff: Tariff): Promise<Tariff> => {
  return createEntity<Tariff>({
    entity: tariff,
    functionName: 'createTariff',
    isEntity: isTariff,
    path: '/resource/tariff',
  });
};

/**
 * @description Updates an existing tariff.
 * @param {Tariff} tariff - The tariff data.
 * @returns {Promise<Tariff>} The updated tariff.
 */
export const updateTariff = async (tariff: Tariff): Promise<Tariff> => {
  return updateEntity<Tariff>({
    entity: tariff,
    functionName: 'updateTariff',
    isEntity: isTariff,
    path: '/resource/tariff',
  });
};

/**
 * Starts an asynchronous tariff ingestion job for a supported source format.
 */
export const bulkIngestTariffs = async (
  source: TariffIngestSource
): Promise<BulkTariffIngestJob> => {
  const data = await post<typeof bulkTariffIngestJobSchema>(
    'QBilling',
    `/resource/tariff/bulk`,
    source
  );
  if (!data) {
    throw new Error('Tariff bulk ingest returned no job');
  }
  return bulkTariffIngestJobSchema.parse(data);
};

export const getBulkTariffIngestJob = async ({
  jobId,
}: {
  jobId: string;
}): Promise<BulkTariffIngestJob> => {
  const data = await get<typeof bulkTariffIngestJobSchema>(
    'QBilling',
    `/resource/tariff/bulk${queryString({ jobId })}`
  );
  if (!data) {
    throw new Error('Tariff bulk ingest job was not found');
  }
  return bulkTariffIngestJobSchema.parse(data);
};

/**
 * @description Lists all tariffs.
 * @returns {Promise<Tariff[]>} An array of tariffs.
 */
export const listTariffs = async (): Promise<Tariff[]> =>
  await get<Tariff[]>('QBilling', `/resource/tariff/all`);

/**
 * @description Swaps the existing tariff on a claim with a new tariff called "matching"
 * @param {string} oldTariff - The existing tariff on the claim.
 * @param {string} newTariff - The new tariff to replace the existing tariff.
 * @param {string} claimID - The ID of the claim that we are swapping tariffs on.
 * @returns {Promise<boolean>} True if the operation was successful, otherwise false.
 */
export const matchTariffs = async ({
  oldTariff,
  newTariff,
  claimID,
}: {
  oldTariff: Tariff;
  newTariff: Tariff;
  claimID: string;
}): Promise<boolean> => {
  const data: { success: boolean } = await put(
    'QBilling',
    `/resource/tariff/match`,
    {
      oldTariff,
      newTariff,
      claimID,
    }
  );
  return data.success;
};

/**
 * @description Hook to list all tariffs.
 * @param {Object} params - The parameters.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Tariff[], Error>} The query result containing an array of tariffs.
 */
export const useListTariffs = ({
  displayNotification = false,
}: {
  displayNotification?: boolean;
}): UseQueryResult<Tariff[], Error> => {
  return useListQuery<Tariff>({
    queryKey: tariffQueryKeys.all,
    fetchFunction: listTariffs,
    functionName: 'listTariffs',
    isEntity: isTariff,
    enabled: true,
    displayNotification,
  });
};

const getPaginatedTariffs = async ({
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<PaginatedResult<Tariff>> =>
  await get<PaginatedResult<Tariff>>(
    'QBilling',
    `/resource/tariff/page${queryString({
      page,
      pageSize,
      filters: convertFiltersToQueryString(filters ?? []),
      globalFilter: convertGlobalFilterToQueryString(globalFilter),
      sorting: convertSortingToQueryString(sorting ?? []),
    })}`
  );

export const usePaginatedTariffs = ({
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: {
  page: number;
  pageSize: number;
  filters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}): UseQueryResult<PaginatedResult<Tariff>, Error> =>
  usePaginatedQuery<Tariff>({
    queryKey: tariffQueryKeys.paginated(
      page,
      pageSize,
      filters,
      globalFilter,
      sorting
    ),
    fetchFunction: getPaginatedTariffs,
    isEntity: isTariff,
    functionName: 'getPaginatedTariffs',
    params: { page, pageSize, filters, globalFilter, sorting },
    enabled: true,
    validationSchema: tariffSchema,
  });

/**
 * @description Fetches a tariff by ID.
 * @param {string} id - The tariff ID.
 * @returns {Promise<Tariff | null>} The tariff data or null if not found.
 */
export const getTariff = async (id: string): Promise<Tariff | null> =>
  await get<Tariff>('QBilling', `/resource/tariff${queryString({ id })}`);

/**
 * @description Hook to fetch a tariff by ID.
 * @param {Object} params - The parameters.
 * @param {string} params.id - The tariff ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Tariff, Error>} The query result containing the tariff data or null if not found.
 */
export const useGetTariff = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Tariff, Error> => {
  return useGetQuery<Tariff>({
    queryKey: tariffQueryKeys.get(id),
    fetchFunction: getTariff,
    functionName: 'getTariff',
    params: id,
    isEntity: isTariff,
    enabled: !!id,
    displayNotification,
    validationSchema: tariffSchema,
  });
};

export const searchTariffs = async ({
  searchValue,
  specialtyCode,
  serviceStart,
  province,
}: {
  searchValue: string;
  specialtyCode: string | undefined;
  serviceStart: string | undefined;
  province: string;
}): Promise<Tariff[]> => {
  if (searchValue === '') return Promise.resolve([]);
  return await get<Tariff[]>(
    'QBilling',
    `/resource/tariff/search${queryString({
      searchValue,
      specialtyCode,
      serviceStart,
      province,
    })}`
  );
};

export const listTariffsByProvider = async ({
  providerID,
  userID,
  siteID,
}: {
  providerID?: string;
  userID?: string;
  siteID?: string;
}): Promise<Tariff[]> => {
  return await get<Tariff[]>(
    'QBilling',
    `/resource/tariff/provider${queryString({ providerID, userID, siteID })}`
  );
};

export const searchAllTariffs = async ({
  code,
}: {
  code: string;
}): Promise<AllTariffSearchResult[]> => {
  if (code === '') return Promise.resolve([]);

  return await get<AllTariffSearchResult[]>(
    'QBilling',
    `/resource/tariff/search/all${queryString({ code })}`
  );
};

export const useSearchTariffs = ({
  searchValue,
  specialtyCode,
  serviceStart,
  province,
}: {
  searchValue: string;
  specialtyCode?: string;
  serviceStart?: string;
  province: string;
}): UseQueryResult<Tariff[], Error> => {
  return useListQuery<Tariff>({
    queryKey: tariffQueryKeys.search(
      searchValue,
      specialtyCode,
      serviceStart,
      province
    ),
    fetchFunction: searchTariffs,
    functionName: 'searchTariffs',
    isEntity: isTariff,
    enabled: !!searchValue,
    params: { searchValue, specialtyCode, serviceStart, province },
    displayNotification: false,
  });
};

export const useSearchAllTariffs = ({
  code,
}: {
  code: string;
}): UseQueryResult<AllTariffSearchResult[], Error> => {
  return useQuery<AllTariffSearchResult[], Error>({
    queryKey: tariffQueryKeys.allSearch(code),
    queryFn: () => searchAllTariffs({ code }),
    enabled: !!code,
    retry: false,
  });
};

export const useListTariffsByProvider = ({
  providerID,
  userID,
  siteID,
  displayNotification = false,
}: {
  providerID?: string;
  userID?: string;
  siteID?: string;
  displayNotification?: boolean;
}): UseQueryResult<Tariff[], Error> => {
  return useListQuery<Tariff>({
    queryKey: tariffQueryKeys.provider(providerID ?? '', userID, siteID),
    fetchFunction: listTariffsByProvider,
    functionName: 'listTariffsByProvider',
    isEntity: isTariff,
    enabled: !!providerID || !!userID,
    params: { providerID, userID, siteID },
    displayNotification,
    validationSchema: tariffSchema,
  });
};

/**
 * @description Hook to create a new tariff.
 * @returns {UseMutationResult<Tariff, Error>} The mutation result for creating a tariff.
 */
export const useCreateTariff = (): UseMutationResult<Tariff, Error> => {
  return useMutationQuery<Tariff>({
    mutationFn: createTariff,
    errorMessage: 'Tariff Creation Failed',
    successMessage: 'Tariff created successfully',
    queryKey: tariffQueryKeys.all,
  });
};

/**
 * @description Hook to update an existing tariff.
 * @returns {UseMutationResult<Tariff, Error>} The mutation result for updating a tariff.
 */
export const useUpdateTariff = (): UseMutationResult<Tariff, Error> => {
  return useMutationQuery<Tariff>({
    mutationFn: updateTariff,
    errorMessage: 'Tariff Update Failed',
    successMessage: 'Tariff Updated',
    queryKey: tariffQueryKeys.all,
  });
};

/**
 * @description Hook to match an existing tariff new tariff.
 * @returns {UseMutationResult<Tariff, Error>} The mutation result for creating a tariff.
 */
export const useMatchTariffs = (): UseMutationResult<boolean, Error> => {
  return useMutationQuery<boolean>({
    mutationFn: matchTariffs,
    errorMessage: 'An error occurred while matching tariffs',
    successMessage: 'Tariffs successfully matched',
    queryKey: tariffQueryKeys.all,
  });
};
